import { KeyValue, KeyValuePipe, NgClass, NgFor, NgIf, NgStyle, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  forwardRef,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  inject,
  SimpleChange,
} from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import { GenericHandleBarModel, filterIfHandlebarNotRendered } from '../services/handlebar-model.service';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import {
  DropdownMenuDirection,
  InputLabelOptions,
  TypographyKeys,
} from '@finxone-platform/shared/sys-config-types';
import { HlmInputDirective } from '@spartan-ng/ui-input-helm';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
import { ClickOutsideModule } from '../directive/click-outside-directive.module';
import { InputValidationFactory } from '../input-field-validation/input-validations-factory';
import { ValidationConfig } from '../input-field-validation/validation-config.type';
import { renderOptionsWithHandlebars } from '../input-options/input-options.utils';
import { SafeHtmlModule } from '../pipes/safeHtml-pipe.module';
import { TooltipComponent } from '../tooltip/tooltip.component';
/**
 * FinxoneSelectDropdownComponent is a customizable dropdown component designed for use within Angular applications. It extends the InputValidationFactory and implements ControlValueAccessor, making it suitable for use with Angular forms. This component supports both single and multiple selections, custom templates for options and error messages, and integrates with form controls.
 * Documentation for this component could be found here:
 * https://dev.azure.com/finxone/Finxone%20Platform/_wiki/wikis/Finxone-Platform.wiki/357/Finxone-select-dropdown-component-usage-guide
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finxone-select-dropdown',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxoneSelectDropdownComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div (clickOutside)="closeDropdown($event)" class="custom-select-wrapper input-container">
      <!-- Select Box -->
      @if (label && labelType !== labelTypeOptions.PLACEHOLDER) {
        @if (labelType === labelTypeOptions.STATIC) {
          <div class="d-flex gap-1 align-items-center">
            <ng-container [ngTemplateOutlet]="labelTemplate"></ng-container>
            @if (inputLabelTooltipText?.length) {
              <finxone-platform-tooltip
                [content]="inputLabelTooltipText"
                [textStyleClass]="tooltipTextStyleClass"
                [borderRadiusClass]="tooltipBorderRadiusClass"
              ></finxone-platform-tooltip>
            }
          </div>
        } @else {
          <ng-container [ngTemplateOutlet]="labelTemplate"></ng-container>
        }
      }

      <ng-template #labelTemplate>
        <span
          [class.labelActive]="isOpen"
          [class.floatingLabelWithValue]="selectedOption?.length && labelType === labelTypeOptions.FLOATING"
          [class.floatingLabel]="labelType === labelTypeOptions.FLOATING"
          class="labelText labelIdle labelConfig"
          (click)="handleLabelClick()"
        >
          {{ label }}
        </span>
      </ng-template>
      <div
        hlmInput
        class="custom-select-box d-flex is-space-between"
        [class.focus]="isOpen"
        [class.disabled]="isDisabled()"
        (click)="toggleDropdown()"
        [class]="textStylingClass + ' ' + borderRadiusClass"
        tabindex="0"
        (blur)="onBlur()"
        (keydown)="onKeyDown($event)"
        [id]="id"
        [ngStyle]="dropdownInputCustomStyle"
      >
        <ng-container *ngIf="!customSelectedOptionTemplate; else customSelectedOption">
          <span
            class="placeholder"
            [innerHTML]="
              dropdownItemSelectedTemplate() ||
                (labelType === labelTypeOptions.PLACEHOLDER
                  ? label
                  : labelType === labelTypeOptions.FLOATING
                    ? ''
                    : placeholder) | safeHtml
            "
          ></span>
        </ng-container>
        <ng-template
          #customSelectedOption
          [ngTemplateOutlet]="customSelectedOptionTemplate"
          [ngTemplateOutletContext]="{ $implicit: dropdownItemSelectedTemplate() }"
        ></ng-template>
        <div *ngIf="selectedOption === ''"></div>
        <span class="arrow">
          <mat-icon svgIcon="chevron-up"></mat-icon>
        </span>
      </div>

      <!-- Dropdown -->
      @if (isOpen) {
        <div #dropdown class="dropdown finxone-select-dropdown" [ngClass]="borderRadiusClass">
          <!-- Search Input -->
          @if (searchEnabled) {
            <div class="search-container">
              <input
                type="text"
                class="inputField"
                [placeholder]="'Search...'"
                [value]="searchQuery"
                (input)="onSearchInputChange($event)"
              />
            </div>
          }

          <ng-container *ngIf="this.options?.length; else noOptionsTemplateContainer">
            @if (!this.searchQuery.length) {
              <ng-container
                *ngTemplateOutlet="defaultOptionsListTemplate; context: { $implicit: options }"
              ></ng-container>
            } @else {
              <ng-container
                *ngTemplateOutlet="searchedOptionsListTemplate; context: { $implicit: options }"
              ></ng-container>
            }
          </ng-container>

          <ng-template #noOptionsTemplateContainer>
            <ng-container *ngIf="customNoOptionsTemplate; else noOptionsTemplate">
              <ng-container [ngTemplateOutlet]="customNoOptionsTemplate"></ng-container>
            </ng-container>
            <ng-template #noOptionsTemplate>
              <div class="no-options">No options available</div>
            </ng-template>
          </ng-template>
          <!-- Optional footer template rendered below the list -->
          <ng-container *ngIf="footerTemplate && isFooterFloating">
            <div class="dropdown-footer p-1 floating" [ngTemplateOutlet]="footerTemplate"></div>
          </ng-container>
        </div>
      }

      <!-- Error Template -->
      <div
        *ngIf="control?.invalid && (control?.dirty || control?.touched)"
        class="error-container"
        role="alert"
      >
        @for (error of control.errors | keyvalue; track error.key) {
          <small class="typog-xs text-error">
            {{ getErrorMessageBasedOnKey(error) }}
          </small>
        }

        <!-- Custom Error Template -->
        <ng-container
          *ngIf="customErrorTemplate"
          [ngTemplateOutlet]="customErrorTemplate"
          [ngTemplateOutletContext]="{
            $implicit: {
              control: control,
              errors: control.errors,
            },
          }"
        ></ng-container>
      </div>

      <ng-template #noOptionsTemplate>
        <div class="no-options">No options available</div>
      </ng-template>

      <ng-template #defaultOptionsListTemplate let-options>
        <div
          class="list-with-scroll"
          [class.show-scrollbar]="this.isScrollable"
          [ngClass]="borderRadiusClass"
        >
          <div
            *ngFor="let option of this.options; let i = index"
            class="dropdown-option"
            (click)="this.selectOption(option)"
            [ngClass]="textStylingClass"
            [class.focused]="i === focusedIndex"
          >
            <input
              *ngIf="this.multiple"
              type="checkbox"
              [checked]="isSelected(option)"
              (click)="$event.stopPropagation()"
            />
            <ng-container *ngIf="!customOptionTemplate; else customOption">
              <span [innerHTML]="renderedOptionLabels[option[optionValueKey]] | safeHtml"> </span
            ></ng-container>
            <ng-template
              #customOption
              [ngTemplateOutlet]="customOptionTemplate"
              [ngTemplateOutletContext]="{ $implicit: option }"
            ></ng-template>
          </div>

          <ng-container *ngIf="footerTemplate && !isFooterFloating">
            <div class="dropdown-footer p-1" [ngTemplateOutlet]="footerTemplate"></div>
          </ng-container>
        </div>
      </ng-template>

      <ng-template #searchedOptionsListTemplate let-options let-searchQuery="searchQuery">
        <div
          class="list-with-scroll"
          [class.show-scrollbar]="this.isScrollable"
          [ngClass]="borderRadiusClass"
        >
          @for (option of searchedOptions; track option) {
            <div class="dropdown-option" (click)="this.selectOption(option)" [ngClass]="textStylingClass">
              <input
                *ngIf="this.multiple"
                type="checkbox"
                [checked]="isSelected(option)"
                (click)="$event.stopPropagation()"
              />
              <ng-container *ngIf="!customOptionTemplate; else customOption">
                <span [innerHTML]="renderedOptionLabels[option[optionValueKey]] | safeHtml"> </span
              ></ng-container>
              <ng-template
                #customOption
                [ngTemplateOutlet]="customOptionTemplate"
                [ngTemplateOutletContext]="{ $implicit: option }"
              ></ng-template>
            </div>
          } @empty {
            <div class="p-3 body1 textOnSurfaceMuted">No results matching your query</div>
          }

          <ng-container *ngIf="footerTemplate && !isFooterFloating">
            <div class="dropdown-footer p-1" [ngTemplateOutlet]="footerTemplate"></div>
          </ng-container>
        </div>
      </ng-template>
      <!-- Info  Template -->
      <ng-container *ngIf="infoText">
        <div [class.d-none]="isOpen" [innerHTML]="infoText | safeHtml"></div>
      </ng-container>
    </div>
  `,
  styles: `
    .custom-select-wrapper {
      position: relative;
      .custom-select-box {
        cursor: pointer;

        .placeholder {
          flex: 1;
          line-height: 2.2rem;
        }

        .arrow {
          font-size: inherit;
          float: right;
          position: relative;
          rotate: 180deg;
          display: flex;
          transition: ease all 0.3s;
        }
        &.focus {
          .arrow {
            rotate: 0deg;
          }
        }
      }

      .no-options {
        text-align: center;
        padding: 10px;
        color: var(--color-on-surface-muted);
      }
    }
  `,
  standalone: true,
  imports: [
    NgIf,
    NgFor,
    NgTemplateOutlet,
    NgClass,
    NgStyle,
    MatIcon,
    HlmInputDirective,
    SafeHtmlModule,
    ClickOutsideModule,
    KeyValuePipe,
    TooltipComponent,
  ],
})
export class FinxoneSelectDropdownComponent
  extends InputValidationFactory
  implements ControlValueAccessor, OnDestroy, OnChanges, AfterViewInit, OnInit
{
  private readonly cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);
  private renderer = inject(Renderer2);
  private elRef = inject(ElementRef);
  private readonly injector = inject(Injector);

  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  @ViewChild('dropdown', { static: false }) dropdownElement!: ElementRef; // Dropdown reference
  @Input() options: any[] = []; // List of options
  @Input() id = ''; // id for dropdown
  @Input() name = ''; // name for dropdown
  @Input() optionLabelKey = 'label'; // Key for displaying label
  @Input() optionValueKey = 'value'; // Key for storing value
  @Input() multiple = false; // Enable multiple selection
  @Input() placeholder = ''; // Placeholder
  @Input() label = 'Select an option'; // label
  @Input() selectedOption = '';
  @Input() customOptionTemplate!: TemplateRef<any> | null; // Custom option template
  @Input() customSelectedOptionTemplate!: TemplateRef<any> | null; // Custom selected option template
  @Input() customErrorTemplate!: TemplateRef<any>; // Error template
  @Input() appendToBody = false;
  @Input() searchEnabled = false;
  @Input() formControlInvalid = false;
  @Input() infoText = '';
  @Input() inputLabelTooltipText = '';
  @Input() dropdownWidth: number;
  @Input() customNoOptionsTemplate!: TemplateRef<any>;
  @Input() isDropdownDisabled = false;
  @Input() isScrollable = true;
  @Input() isScrollPeekEnabled = false;
  @Input() visibleItemsCount = 5;
  @Input() menuDirection = DropdownMenuDirection.AUTO;
  @Input() dropdownInputCustomStyle: Record<string, unknown>;
  @Input() footerTemplate?: TemplateRef<ElementRef> | null; // Optional footer template rendered at bottom of dropdown
  @Input() footerFloatingThreshold?: number;
  public searchQuery = '';
  public tooltipBorderRadiusClass = '';
  public tooltipTextStyleClass = '';

  // Validation Configuration
  @Input() validationConfig: ValidationConfig[] = [];

  @Output() selectionChange = new EventEmitter<string>();

  public selectedValues: any[] = []; // For multiple selections
  public isOpen = false; // Dropdown visibility toggle
  public touched = false;
  public labelType: InputLabelOptions;
  public labelTypeOptions = InputLabelOptions;
  public textStylingClass: TypographyKeys;
  private cssVars: Record<string, string> = {};

  private innerValue: any = null;
  public isDisabled = signal(false);
  public borderRadiusClass = '';
  public searchedOptions: any[];
  public dropdownInputHeight: string;
  public handlebarModel: GenericHandleBarModel;
  private configuredVisibleItemsCount = this.visibleItemsCount;

  public get isFooterFloating(): boolean {
    if (!this.footerTemplate) return false;
    // If no threshold specified, footer should float
    if (this.footerFloatingThreshold == null) {
      return true;
    }
    return Array.isArray(this.options) && this.options.length > this.footerFloatingThreshold;
  }

  // Getter and setter for ControlValueAccessor
  get value(): any {
    return this.innerValue;
  }
  set value(val: any) {
    if (val !== this.innerValue) {
      this.innerValue = val;
      this.onChange(val);
      this.selectionChange.emit(val);
    }
  }

  public readonly control = new FormControl();

  // ControlValueAccessor methods
  // eslint-disable-next-line @typescript-eslint/ban-types
  onChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types
  onTouched: Function = () => {};

  private dropdownClone!: HTMLElement | null;
  private scrollListener?: () => void;
  private repositionTimeout: any;
  private lastKey: string | null = null;
  public focusedIndex = -1;
  public renderedOptionLabels: Record<string, string> = {};

  constructor() {
    super();
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      if (globalCssVariables) {
        this.cssVars = globalCssVariables;
        //get the label type and text style class from root styles
        this.labelType = this.getCssVariableValue('--inputFieldLabelType') as InputLabelOptions;
        this.textStylingClass = this.getCssVariableValue('--inputFieldTextStylingClass') as TypographyKeys;
        this.borderRadiusClass = this.getCssVariableValue('--inputFieldBorderRadiusClass');
        this.dropdownInputHeight = this.getCssVariableValue('--inputFieldHeight');
        this.tooltipBorderRadiusClass = this.getCssVariableValue('--tooltipBorderRadiusClass');
        this.tooltipTextStyleClass = this.getCssVariableValue('--tooltipTextStyleClass');
        this.cd.detectChanges();
      }
    });
  }
  ngAfterViewInit(): void {
    this.setDisabledState(this.isDropdownDisabled);
  }

  ngOnInit(): void {
    this.renderAllOptionLabels();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']?.currentValue !== undefined) {
      this.control.setValue(changes['value']?.currentValue);
    }
    if (changes['options']?.currentValue !== undefined && this.value) {
      this.updateFocusedIndex(this.value);
    }
    if (changes['validationConfig']?.currentValue !== undefined) {
      const validators = this.setupValidation(this.validationConfig);
      this.control.setValidators(validators);
      this.control.updateValueAndValidity();
    }
    if (changes['formControlInvalid']?.currentValue !== undefined) {
      this.setFormControlValidity(this.formControlInvalid);
    }

    if (changes['visibleItemsCount']) {
      this.configuredVisibleItemsCount = this.visibleItemsCount;
    }

    if (changes['options']?.currentValue !== changes['options']?.previousValue) {
      this.renderAllOptionLabels();
      this.refreshDropdownListHeightOnOptionsChange(changes['options']);
    }
  }

  /**
   * Keeps the dropdown list's max-height in sync with the current options.
   *
   * `assignMaxHeightToDropdownList` permanently clamps `visibleItemsCount` to the number of
   * options rendered, so a new longer list would still be sized for the previous shorter one and
   * open clipped. Restoring the configured count makes it measure again, once the new options
   * are in the DOM (only needed while open — a closed panel is measured on open).
   *
   * @param optionsChange - The `options` entry from `ngOnChanges`.
   */
  private refreshDropdownListHeightOnOptionsChange(optionsChange: SimpleChange): void {
    // Only a change in the number of options can invalidate the height.
    if (optionsChange.currentValue?.length === optionsChange.previousValue?.length) {
      return;
    }

    this.visibleItemsCount = this.configuredVisibleItemsCount;

    if (this.isOpen) {
      // Re-measure after the new options are rendered, since the height is read off the items.
      afterNextRender(() => this.resetDropdownListMaxHeight(this.appendToBody), {
        injector: this.injector,
      });
    }
  }

  /**
   * Renders the currently selected dropdown option using Handlebars.
   *
   * This method:
   *  - Checks if a selected option exists and if the Handlebars model has been built.
   *  - Compiles the `selectedOption` string as a Handlebars template.
   *  - Renders the template with the current `handlebarModel`.
   *  - Uses `filterIfHandlebarNotRendered` to ensure that if the template didn't produce
   *    any dynamic output, a safe fallback (empty string) is returned.
   *
   * @returns The rendered label for the selected dropdown option, or an empty string if unavailable.
   */
  public dropdownItemSelectedTemplate(): string {
    if (!this.selectedOption || !this.handlebarModel) {
      return '';
    }
    const template = Handlebars.compile(this.selectedOption);
    return filterIfHandlebarNotRendered(template(this.handlebarModel)) ?? '';
  }

  private renderAllOptionLabels(): void {
    renderOptionsWithHandlebars(this.options, this.optionLabelKey, this.optionValueKey, this.injector)
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.renderedOptionLabels = data?.renderedOptions;
        this.handlebarModel = data?.model;
        this.cd.markForCheck();
      });
  }

  private setFormControlValidity(formControlInvalid: boolean) {
    if (!formControlInvalid) {
      this.control.markAsPristine();
      this.control.markAsUntouched();
    } else {
      this.control.markAsDirty();
      this.control.markAsTouched();
    }
  }

  private initDropdownElement() {
    if (this.appendToBody) {
      this.appendDropdownToBody();
    } else {
      console.error('Dropdown element is not defined');
    }
  }

  private appendDropdownToBody(): void {
    // Clone the dropdown instead of reusing the original
    this.dropdownClone = this.dropdownElement.nativeElement;

    // Append the cloned dropdown to body
    document.body.appendChild(this.dropdownClone as never);

    // Add class for styling
    const parentClass = 'finxone-select-dropdown';
    this.renderer.addClass(this.dropdownClone, parentClass);

    // Style & Positioning
    const rect = this.elRef.nativeElement.getBoundingClientRect();
    this.renderer.setStyle(this.dropdownClone, 'position', 'absolute');

    // Determine the actual direction to use
    const direction = this.getActualMenuDirection(rect);

    // Position the dropdown based on direction
    this.positionDropdown(rect, direction, this.dropdownClone);

    this.renderer.setStyle(this.dropdownClone, 'left', `${rect.left}px`);
    this.renderer.setStyle(this.dropdownClone, 'z-index', '100001');
    const width = this.dropdownWidth || rect.width;
    this.renderer.setStyle(this.dropdownClone, 'width', `${width}px`);
    this.renderer.setStyle(this.dropdownClone, 'display', 'block');

    // Ensure input field styles
    const inputEl = this.dropdownClone?.querySelector('input');
    inputEl?.classList.add('inputField');

    // Apply dropdown list max height after appending to body to prevent flash of full list
    this.resetDropdownListMaxHeight(true);

    // Use setTimeout to ensure it's updated in the next tick
    setTimeout(() => {
      this.cd.detectChanges();
    });
  }

  public writeValue(value: any): void {
    this.innerValue = value;
    this.selectedValues = Array.isArray(value) ? value : [value];
    if (value) {
      this.updateFocusedIndex(value);
    } else {
      // Control cleared from outside — drop the highlight instead of leaving it
      // on the option this dropdown held before.
      this.focusedIndex = -1;
    }
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public toggleDropdown(): void {
    if (!this.isDisabled()) {
      this.isOpen = !this.isOpen;
      if (!this.isOpen) {
        this.searchQuery = ''; // Reset search when closing dropdown
        this.removeScrollListener();
      } else {
        // if appendToBody is false, set the max height and position of the dropdown list
        // else it will be handled by appendDropdownToBody method
        if (!this.appendToBody) {
          this.setupDropdownListPositionAndMaxHeight();
        }
        // when scrolling is disabled don't set scroll listeners
        // it will hide the dropdown as soon as the user scrolls the page to see the remaining items
        if (this.isScrollable) this.addScrollListener();
      }
    }
    // Use requestAnimationFrame to manage DOM updates efficiently
    requestAnimationFrame(() => {
      if (this.isOpen) {
        this.initDropdownElement(); // Initialize the dropdown element when it is open
      } else {
        this.cleanUpElement(); // Remove and clean up the dropdown element when it is closed
      }
      this.cd.detectChanges(); // Trigger Angular's change detection to ensure the UI reflects changes
    });
    this.cd.detectChanges(); // Trigger Angular's change detection to ensure the UI reflects changes
  }

  public selectOption(option: any): void {
    if (this.multiple) {
      if (this.selectedValues.includes(option[this.optionValueKey])) {
        this.selectedValues = this.selectedValues.filter((val) => val !== option[this.optionValueKey]);
      } else {
        this.selectedValues.push(option[this.optionValueKey]);
      }
      this.value = this.selectedValues;
      this.selectedOption = this.getSelectedValuesLabels();
    } else {
      this.value = option[this.optionValueKey];
      this.isOpen = false;
      this.cleanUpElement();
      this.selectedOption = this.getLabelByValue(this.value);
    }
    this.updateFocusedIndex(this.value);
    this.selectionChange.emit(this.value);
    this.touched = true;
    this.onTouched();
  }

  private updateFocusedIndex(value: any) {
    this.focusedIndex = this.options.findIndex((item) => item[this.optionValueKey] === value);
  }

  public onKeyDown(event: KeyboardEvent): void {
    this.lastKey = event.key; // Store what key was pressed

    if (this.isDisabled()) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else if (this.focusedIndex >= 0) {
          this.selectOption(this.options[this.focusedIndex]);
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else {
          this.focusedIndex = this.focusedIndex < this.options.length - 1 ? this.focusedIndex + 1 : 0;
          this.scrollToFocused();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen) {
          this.toggleDropdown();
        } else {
          this.focusedIndex = this.focusedIndex > 0 ? this.focusedIndex - 1 : this.options.length - 1;
          this.scrollToFocused();
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.isOpen = true;
        this.focusedIndex = -1;
        this.toggleDropdown();
        break;
    }
  }

  public onBlur(): void {
    this.onTouched();
    if (this.lastKey === 'Tab') {
      requestAnimationFrame(() => {
        if (!this.activeDropdownCloneElement()) {
          this.isOpen = true;
          this.updateFocusedIndex(this.value);
          this.toggleDropdown();
        }
      });
    }
    // Always reset the key
    this.lastKey = null;
  }

  private activeDropdownCloneElement() {
    const activeElement = document.activeElement;
    return this.dropdownClone?.contains(activeElement);
  }

  private scrollToFocused(): void {
    try {
      const dropdownEl = this.dropdownClone?.querySelector('.list-with-scroll') as HTMLElement;
      const optionEls: HTMLElement[] = Array.from(dropdownEl.querySelectorAll('.dropdown-option'));
      const focusedEl = optionEls[this.focusedIndex];
      focusedEl?.scrollIntoView({ block: 'nearest' });
    } catch (error) {
      console.error('scrollToFocused: error', error);
    }
  }

  public isSelected(option: any): boolean {
    return this.multiple
      ? this.selectedValues.includes(option[this.optionValueKey])
      : this.value === option[this.optionValueKey];
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
    isDisabled ? this.control.disable() : this.control.enable();
  }

  public getSelectedValuesLabels(): string {
    return this.selectedValues.map((val) => this.getLabelByValue(val)).join(', ');
  }

  public getLabelByValue(val: string): string {
    const value = this.options?.find((option) => option[this.optionValueKey] === val)?.[this.optionLabelKey];
    return value ?? '';
  }

  public getErrorMessageBasedOnKey(error: KeyValue<string, any>): string {
    const key = error.key;
    if (!this.validationConfig.some((config) => config.type === key) && typeof error.value === 'string') {
      return error.value;
    } else {
      const matchingConfig = this.validationConfig.find((config) => config.type === key);
      if (matchingConfig) {
        return this.getErrorMessage(matchingConfig);
      }
    }
    return 'Cannot find error message';
  }

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName];
  }

  public handleLabelClick(): void {
    if (this.labelType === this.labelTypeOptions.FLOATING) {
      this.isOpen = true;
    }
  }

  private cleanUpElement() {
    if (this.dropdownClone) {
      this.renderer.removeChild(document.body, this.dropdownClone);
      this.dropdownClone = null;
    }
  }

  ngOnDestroy(): void {
    // Cleanup cloned dropdown if it exists
    this.cleanUpElement();
    // Cleanup scroll listener
    this.removeScrollListener();
  }

  /**
   * Handles the search input change event by updating the search query
   * and filtering the available options based on the query.
   * Filters options by checking if either the label or value contains the search query.
   * The search is case-insensitive and works with partial matches.
   *
   * @param event - The input event from the search field.
   */
  onSearchInputChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;

    if (!this.options?.length) {
      this.searchedOptions = [];
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.searchedOptions = this.options.filter((option) => {
      const label = String(option[this.optionLabelKey] || '').toLowerCase();
      const value = String(option[this.optionValueKey] || '').toLowerCase();
      return label.includes(query) || value.includes(query);
    });

    this.cd.markForCheck();
  }

  public closeDropdown(event: Event) {
    const clickedInsideDropdown = this.dropdownElement?.nativeElement.contains(event.target);
    if (clickedInsideDropdown) {
      return; // Ignore clicks inside the dropdown
    }
    this.isOpen = false;
    this.searchQuery = ''; // Reset search query when closing dropdown
    // this.removeScrollListener();
    this.cleanUpElement();
  }

  /**
   * Add scroll listeners to the window and scrollable parent
   * to reset the dropdown when the user scrolls
   */
  private addScrollListener(): void {
    // Find the scrollable parent container (likely the modal)
    const scrollableParent = this.findScrollableParent(this.elRef.nativeElement);

    const scrollListeners: (() => void)[] = [];

    // Listen to window scroll
    scrollListeners.push(
      this.renderer.listen('window', 'scroll', () => {
        this.resetDropdown();
      }),
    );

    // Listen to scrollable parent if found
    if (scrollableParent) {
      scrollListeners.push(
        this.renderer.listen(scrollableParent, 'scroll', () => {
          this.resetDropdown();
        }),
      );
    }

    // Listen to document scroll
    scrollListeners.push(
      this.renderer.listen('document', 'scroll', () => {
        this.resetDropdown();
      }),
    );

    // Store cleanup function for all listeners
    this.scrollListener = () => {
      scrollListeners.forEach((cleanup) => cleanup());
    };
  }

  /**
   * Recursively check for parent elements to find the scrollable parent
   * @param element - The current element to check
   * @returns The scrollable parent element or null if not found
   */
  private findScrollableParent(element: HTMLElement): HTMLElement | null {
    if (!element || element === document.body) return null;

    const style = window.getComputedStyle(element);
    const isScrollable =
      style.overflow === 'auto' ||
      style.overflow === 'scroll' ||
      style.overflowY === 'auto' ||
      style.overflowY === 'scroll' ||
      style.overflowX === 'auto' ||
      style.overflowX === 'scroll';

    if (isScrollable) {
      return element;
    }

    return element.parentElement ? this.findScrollableParent(element.parentElement) : null;
  }

  /**
   * Reset the dropdown by toggling it if it is open
   */
  private resetDropdown(): void {
    if (this.activeDropdownCloneElement()) {
      // Prevent the dropdown from closing **so that** the user can interact with the dropdown's contents without it disappearing immediately.
      return;
    }

    if (this.isOpen && this.dropdownClone) {
      this.toggleDropdown();
    }
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = undefined;
    }
    // Clear any pending reposition timeout
    if (this.repositionTimeout) {
      this.repositionTimeout = undefined;
    }
  }

  private resetDropdownListMaxHeight(forCloneElement = false): void {
    const parentElement = forCloneElement ? this.dropdownClone : this.dropdownElement.nativeElement;
    if (parentElement) {
      // Find element with class 'list-with-scroll' within the parent element
      const dropdownList: HTMLElement = parentElement.querySelector('.list-with-scroll');
      if (!dropdownList || dropdownList === null) return;
      this.assignMaxHeightToDropdownList(dropdownList);
    }
  }

  /**
   * This is used when appendToBody is false
   * To setup the dropdown list position and max height
   */
  private setupDropdownListPositionAndMaxHeight(): void {
    this.resetDropdownListMaxHeight();
    const rect = this.elRef.nativeElement.getBoundingClientRect();
    const direction = this.getActualMenuDirection(rect);
    this.positionDropdown(rect, direction, this.dropdownElement.nativeElement);
  }

  /**
   * Determines the actual menu direction based on the menuDirection setting and viewport space
   * @param rect - The bounding rectangle of the select box element
   * @returns The actual direction to use (UP or DOWN)
   */
  private getActualMenuDirection(rect: DOMRect): DropdownMenuDirection {
    if (this.menuDirection === DropdownMenuDirection.UP) {
      return DropdownMenuDirection.UP;
    }

    if (this.menuDirection === DropdownMenuDirection.DOWN) {
      return DropdownMenuDirection.DOWN;
    }

    // AUTO mode: calculate based on available space
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Estimate dropdown height (use a reasonable default or calculate from options)
    const estimatedDropdownHeight = this.estimateDropdownHeight();

    // If there's more space above and not enough space below, open upward
    if (spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow) {
      return DropdownMenuDirection.UP;
    }

    // Default to opening downward
    return DropdownMenuDirection.DOWN;
  }

  /**
   * Gets the actual or estimated height of the dropdown
   * If the dropdown element exists in DOM, returns its actual height
   * Otherwise, estimates based on visible items count and item height
   * @returns Height in pixels
   */
  private estimateDropdownHeight(): number {
    // If dropdown clone exists, read its actual height
    if (this.dropdownClone?.offsetHeight) {
      return this.dropdownClone.offsetHeight;
    }

    // Fallback to estimation if element not yet rendered
    const defaultItemHeight = 40;
    const searchBarHeight = this.searchEnabled ? 48 : 0;
    const footerHeight = this.footerTemplate ? 40 : 0;

    const itemsHeight = this.visibleItemsCount * defaultItemHeight;
    const peekHeight = this.isScrollPeekEnabled ? defaultItemHeight / 2 : 0;

    return searchBarHeight + itemsHeight + peekHeight + footerHeight;
  }

  /**
   * Positions the dropdown above or below the select box based on direction
   * @param rect - The bounding rectangle of the select box element
   * @param direction - The direction to position the dropdown (UP or DOWN)
   * @param dropdownElement - The dropdown element to position
   */
  private positionDropdown(
    rect: DOMRect,
    direction: DropdownMenuDirection,
    dropdownElement: HTMLElement | null,
  ): void {
    if (!dropdownElement) return;
    if (direction === DropdownMenuDirection.DOWN) {
      const topPosition = rect.bottom + window.scrollY;
      this.renderer.setStyle(dropdownElement, 'top', `${topPosition}px`);
    } else {
      // Position above the select box
      // Calculate: rect.top (position from top) - dropdown height
      // We need to wait for the dropdown to render to get its actual height
      setTimeout(() => {
        const dropdownHeight = this.estimateDropdownHeight();
        const topPosition = rect.top + window.scrollY - dropdownHeight;
        this.renderer.setStyle(dropdownElement, 'top', `${topPosition}px`);
      }, 0);
    }
  }

  private assignMaxHeightToDropdownList(dropdownList: HTMLElement): void {
    if (this.isScrollable && this.visibleItemsCount) {
      const itemsCount = dropdownList.childElementCount;
      if (itemsCount < this.visibleItemsCount) {
        this.visibleItemsCount = itemsCount;
      }
      const firstElementInsideParent = dropdownList.firstChild;
      const childElementHeight =
        firstElementInsideParent && (firstElementInsideParent as HTMLElement)?.offsetHeight !== 0
          ? (firstElementInsideParent as HTMLElement).offsetHeight
          : 40;
      let heightToApply = this.visibleItemsCount * childElementHeight;
      //if scrollpeek is enabled then add half of the child element height to make next item partially visible
      if (this.isScrollPeekEnabled) {
        heightToApply += childElementHeight / 2;
      }
      // Set max-height to show only the specified number of items
      this.renderer.setStyle(dropdownList, 'max-height', `${heightToApply}px`);
    } else {
      // Set max-height to unset for the element to show complete list at once
      this.renderer.setStyle(dropdownList, 'max-height', 'unset');
    }
  }
}
